import { useState, useEffect, useCallback } from 'react';
import {
  X, Share2, Download, Copy, Check, Trash2, Loader, Link2,
  FileText, FilePlus, AlertCircle, ExternalLink,
} from 'lucide-react';
import { toast } from 'react-toastify';
import fetchWithTenant from '../../utils/fetchWithTenant';

const EXPIRY_OPTIONS = [
  { value: '',     label: 'Never expires' },
  { value: '7',    label: '7 days' },
  { value: '30',   label: '30 days' },
  { value: '90',   label: '90 days' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function qrUrlFor(shareUrl) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(shareUrl)}`;
}

/** Tiny inline copy-to-clipboard input. */
function CopyField({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };
  return (
    <div className="share-copy-field">
      <input type="text" readOnly value={value} onFocus={e => e.target.select()} />
      <button type="button" className="share-copy-btn" onClick={copy} title="Copy">
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────
// SHARE TAB
// ─────────────────────────────────────────────

function ShareTab({ caseId, evidenceCount }) {
  const [tokens, setTokens]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [viewerName, setViewerName] = useState('');
  const [expiresIn, setExpiresIn] = useState('');
  const [creating, setCreating]   = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [latest, setLatest]       = useState(null);

  const loadTokens = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchWithTenant(`/api/investigations/cases/${caseId}/share`);
      if (!r.ok) throw new Error('Failed to load share links');
      setTokens(await r.json());
    } catch {
      toast.error('Failed to load share links');
      setTokens([]);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { loadTokens(); }, [loadTokens]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const r = await fetchWithTenant(`/api/investigations/cases/${caseId}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          viewerName: viewerName.trim() || undefined,
          expiresInDays: expiresIn ? Number(expiresIn) : undefined,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to generate link');
      }
      const created = await r.json();
      setLatest(created);
      setTokens(prev => [created, ...prev]);
      setViewerName('');
      toast.success('Share link generated');
    } catch (err) {
      toast.error(err.message || 'Failed to generate link');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (tokenId) => {
    setRevokingId(tokenId);
    try {
      const r = await fetchWithTenant(`/api/investigations/cases/${caseId}/share/${tokenId}`, {
        method: 'DELETE',
      });
      if (!r.ok) throw new Error('Failed to revoke');
      setTokens(prev => prev.filter(t => t.id !== tokenId));
      if (latest?.id === tokenId) setLatest(null);
      toast.success('Share link revoked');
    } catch {
      toast.error('Failed to revoke link');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="share-tab-body">
      {/* ── Generator ── */}
      <section className="share-section">
        <h3 className="share-section-title">Generate Share Link</h3>
        <p className="share-section-desc">
          Creates a read-only, tokenized URL anyone can open without an account. Generated links
          appear under <em>Active Links</em> below; revoke them any time.
        </p>

        <div className="share-form-row">
          <div className="share-field">
            <label>Viewer (optional)</label>
            <input
              type="text"
              placeholder="e.g. Law Enforcement, Insurance Adjuster"
              value={viewerName}
              onChange={e => setViewerName(e.target.value)}
              maxLength={200}
              disabled={creating}
            />
          </div>
          <div className="share-field share-field-narrow">
            <label>Expiry</label>
            <select value={expiresIn} onChange={e => setExpiresIn(e.target.value)} disabled={creating}>
              {EXPIRY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <button className="btn-primary share-generate-btn" onClick={handleCreate} disabled={creating}>
          {creating ? <Loader size={14} className="spin" /> : <Link2 size={14} />}
          {creating ? 'Generating…' : 'Generate Link'}
        </button>

        {latest && (
          <div className="share-generated">
            <div className="share-generated-row">
              <CopyField value={latest.shareUrl} />
              <a
                href={latest.shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="share-open-link"
                title="Open in new tab"
              >
                <ExternalLink size={14} />
              </a>
            </div>
            <div className="share-generated-qr">
              <img src={qrUrlFor(latest.shareUrl)} alt="QR code" width={120} height={120} />
              <div className="share-generated-qr-hint">Scan to open on a phone</div>
            </div>
          </div>
        )}
      </section>

      {/* ── Active links ── */}
      <section className="share-section">
        <h3 className="share-section-title">Active Links</h3>
        {loading ? (
          <div className="share-empty"><Loader size={16} className="spin" /> Loading…</div>
        ) : tokens.length === 0 ? (
          <div className="share-empty">No active share links</div>
        ) : (
          <ul className="share-token-list">
            {tokens.map(t => {
              const expired = t.expiresAt && new Date(t.expiresAt).getTime() < Date.now();
              return (
                <li key={t.id} className={`share-token-item ${expired ? 'expired' : ''}`}>
                  <div className="share-token-main">
                    <div className="share-token-name">{t.viewerName || 'Anonymous'}</div>
                    <CopyField value={t.shareUrl} />
                    <div className="share-token-meta">
                      <span>Created {fmtDate(t.createdAt)}</span>
                      <span>·</span>
                      <span>{t.expiresAt ? (expired ? 'Expired' : `Expires ${fmtDate(t.expiresAt)}`) : 'Never expires'}</span>
                      <span>·</span>
                      <span>{t.accessCount} view{t.accessCount === 1 ? '' : 's'}</span>
                    </div>
                  </div>
                  <button
                    className="share-revoke-btn"
                    title="Revoke link"
                    onClick={() => handleRevoke(t.id)}
                    disabled={revokingId === t.id}
                  >
                    {revokingId === t.id ? <Loader size={13} className="spin" /> : <Trash2 size={13} />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {evidenceCount === 0 && (
        <div className="share-info-row">
          <AlertCircle size={14} />
          <span>This case has no evidence items yet — the shared view will be mostly empty.</span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// EXPORT TAB
// ─────────────────────────────────────────────

function ExportTab({ caseId, evidenceByType: initialByType, evidenceCount: initialCount, notesCount }) {
  const [downloading, setDownloading] = useState(false);
  const [byType, setByType] = useState(initialByType || {});
  const [count, setCount]   = useState(initialCount || 0);

  // Refresh byType when the tab mounts so the breakdown reflects the latest
  // gather, not the snapshot the parent had when the modal first opened.
  useEffect(() => {
    let cancelled = false;
    fetchWithTenant(`/api/investigations/cases/${caseId}/gather-status`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (cancelled || !d) return;
        if (d.byType) setByType(d.byType);
        if (typeof d.evidenceCount === 'number') setCount(d.evidenceCount);
      })
      .catch(() => { /* prop fallback is fine */ });
    return () => { cancelled = true; };
  }, [caseId]);

  const handlePdf = async () => {
    // Re-check IQ staleness fresh (uncached) before exporting. Non-blocking on
    // failure; only gates when IQ confirms gathered items now have newer
    // validation state, forcing an explicit decision before the report ships.
    try {
      const sr = await fetchWithTenant(`/api/iq/investigations/${caseId}/staleness`);
      if (sr.ok) {
        const data = await sr.json();
        if (data?.ok && data.checked && data.stale > 0) {
          const proceed = window.confirm(
            `Warning: ${data.stale} of ${data.total} IQ-sourced evidence items ` +
            `have updated validation state since this case was created. Export anyway?`
          );
          if (!proceed) return;
        }
      }
    } catch (_e) {
      // Staleness check failure is non-blocking — proceed with export.
    }

    setDownloading(true);
    try {
      const r = await fetchWithTenant(`/api/investigations/cases/${caseId}/export/pdf`);
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to export PDF');
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `investigation-${caseId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error(err.message || 'Failed to export PDF');
    } finally {
      setDownloading(false);
    }
  };

  const tableRow = (label, count) => (
    <div className="share-export-row" key={label}>
      <span>{label}</span>
      <span className="share-export-count">{count}</span>
    </div>
  );

  return (
    <div className="share-tab-body">
      <section className="share-section">
        <h3 className="share-section-title">Export PDF</h3>
        <p className="share-section-desc">
          Generates a branded report including a cover page, summary, full evidence timeline,
          and notes. VMS clips are included as links back into the VMS.
        </p>

        <div className="share-export-includes">
          {tableRow('ACS events',     byType.ACS_EVENT    || 0)}
          {tableRow('VMS clips',      byType.VMS_CLIP     || 0)}
          {tableRow('FR matches',     byType.FR_MATCH     || 0)}
          {tableRow('Intercom logs',  byType.INTERCOM_LOG || 0)}
          <div className="share-export-row total">
            <span>Total items</span>
            <span className="share-export-count">{count}</span>
          </div>
          {tableRow('Notes', notesCount || 0)}
        </div>

        <button className="btn-primary share-generate-btn" onClick={handlePdf} disabled={downloading}>
          {downloading ? <Loader size={14} className="spin" /> : <Download size={14} />}
          {downloading ? 'Rendering…' : 'Export PDF'}
        </button>

        <div className="share-info-row">
          <AlertCircle size={14} />
          <span>PDF includes all evidence items and notes. VMS clips are included as links.</span>
        </div>
      </section>

      <section className="share-section share-section-disabled">
        <h3 className="share-section-title">Coming later</h3>
        <div className="share-future-row">
          <FilePlus size={14} />
          <span>ZIP Bundle (Phase 5)</span>
          <span className="share-future-badge">Soon</span>
        </div>
        <div className="share-future-row">
          <FileText size={14} />
          <span>CCM Player Package</span>
          <span className="share-future-badge">Future</span>
        </div>
      </section>
    </div>
  );
}

// ─────────────────────────────────────────────
// MODAL SHELL
// ─────────────────────────────────────────────

const TABS = [
  { id: 'share',  label: 'Share',  icon: Share2 },
  { id: 'export', label: 'Export', icon: Download },
];

/**
 * Props:
 *   isOpen, onClose, caseId, evidenceByType, evidenceCount, notesCount
 */
export default function ShareExportModal({ isOpen, onClose, caseId, evidenceByType = {}, evidenceCount = 0, notesCount = 0 }) {
  const [tab, setTab] = useState('share');

  useEffect(() => { if (isOpen) setTab('share'); }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="share-modal">
        <div className="share-modal-header">
          <div className="share-modal-title">
            <Share2 size={16} />
            <span>Share &amp; Export</span>
          </div>
          <button className="share-modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="share-modal-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                className={`share-modal-tab ${tab === t.id ? 'active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                <Icon size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'share' && (
          <ShareTab caseId={caseId} evidenceCount={evidenceCount} />
        )}
        {tab === 'export' && (
          <ExportTab
            caseId={caseId}
            evidenceByType={evidenceByType}
            evidenceCount={evidenceCount}
            notesCount={notesCount}
          />
        )}
      </div>
    </div>
  );
}
